import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { ClientDiaryDataService } from '../../../services/todo/client-diary-data.service';
import { ListItem } from '../../../models/api/core/list/list-item';
import { focusFirstInvalid } from '../../../utilities/focus-first-invalid';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { DatePickerComponent } from '../../core/controls/date-picker.component';
import { SelectComponent } from '../../core/controls/select.component';
import { TextareaComponent } from '../../core/controls/textarea.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../core/labels/required-fields-note.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';

@Component({
  selector: 'app-diary-edit-page',
  imports: [
    LoadingPanelComponent,
    EmptyStateComponent,
    ButtonComponent,
    DatePickerComponent,
    SelectComponent,
    CheckboxComponent,
    TextareaComponent,
    ActionRowComponent,
    ReactiveFormsModule,
    RouterModule,
    PageHeaderComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <app-page-header
      [label]="headerLabel"
      [backRouterLink]="backRouterLink"
      [backLabel]="backLabel"
    />
    <app-loading-panel
      [state]="state"
      [error]="error"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
    >
      <ng-template #empty>
        <app-empty-state icon="fa-history" label="Empty history" height="450px">
          <span>TODO empty state</span>
        </app-empty-state>
      </ng-template>

      <ng-template #loaded>
        <form [formGroup]="fg" #formEl>
          <app-required-fields-note />
          <div class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-2 lg:tw-gap-0">
            <div
              class="tw-flex tw-flex-col tw-w-full lg:tw-w-1/3 tw-min-w-0 tw-gap-2 lg:tw-gap-0"
            >
              <app-select
                [options]="clientOptions"
                optionLabel="description"
                optionValue="value"
                class="tw-basis-1/3"
                [virtualScroll]="true"
                [control]="fg.controls.client"
                label="Client"
                [multiple]="false"
                [disabled]="editMode || isClaimSpecific || isDiaryTiedToClaim"
              />
              <app-select
                [options]="nameOptions"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.followupUser"
                label="Follow-up user"
                class="tw-w-full"
                [multiple]="false"
              />
              <app-select
                [options]="additionalUsersOptions"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.selectedUsers"
                label="Additional follow-up users"
                class="tw-w-full"
                [multiple]="true"
              />
              <app-date-picker
                class="tw-w-full"
                [control]="fg.controls.dueDate"
                label="Due date"
              />
              <app-select
                [options]="diaryTypes"
                optionLabel="description"
                optionValue="value"
                class="tw-w-full"
                [virtualScroll]="false"
                [control]="fg.controls.diaryType"
                label="Diary type"
                [multiple]="false"
              />
              @if (allowComplete) {
                <app-checkbox
                  label="Allow other users to update the diary status"
                  [control]="fg.controls.allowOthers"
                />
              }
              <app-checkbox
                label="Send email reminder"
                [control]="fg.controls.sendEmail"
              />
              @if (headerLabel !== 'New diary' && allowComplete) {
                <app-checkbox
                  label="Completed"
                  [control]="fg.controls.completed"
                />
              }
            </div>
            <div
              class="tw-flex tw-flex-col tw-w-full lg:tw-w-2/3 tw-min-w-0 lg:tw-pl-4"
            >
              <app-textarea
                [control]="fg.controls.defaultText"
                label="Text"
                rows="16"
              ></app-textarea>
            </div>
          </div>
          <app-action-row useMobileLayout>
            <app-button
              right1
              type="submit"
              [loading]="loading"
              label="Save Diary"
              (click)="onSaveDiary()"
            />
            <app-button
              right2
              emphasis="Low"
              [routerLink]="backRouterLink"
              label="Cancel"
            ></app-button>
          </app-action-row>
        </form>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class DiaryEditPageComponent extends BaseComponent implements OnInit {
  private clientDiaryData = inject(ClientDiaryDataService);

  @ViewChild('formEl', { read: ElementRef })
  formEl!: ElementRef<HTMLElement>;

  @Output()
  search = new EventEmitter();

  @Output()
  clear = new EventEmitter();

  @Input()
  loading = false;

  editMode = false;
  isPreloading = false;
  currentDiaryId: number | null = null;
  isClaimSpecific = false;
  isDiaryTiedToClaim = false;

  backRouterLink = '/todo';

  backLabel = 'My to-do';

  headerLabel = 'New diary';

  allowComplete = true;

  protected state: LoadingState = 'Loading';
  error: any = null;

  fg = new FormGroup({
    client: new FormControl<any>(null, [Validators.required]),
    followupUser: new FormControl<string>(null, [Validators.required]),
    dueDate: new FormControl<Date>(null, Validators.required),
    diaryType: new FormControl<ListItem>(null, Validators.required),
    allowOthers: new FormControl<boolean>(true),
    sendEmail: new FormControl<boolean>(false),
    defaultText: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(10000),
    ]),
    tempUser: new FormControl<string>(null),
    selectedUsers: new FormControl<string[]>(null),
    completed: new FormControl<boolean>(false),
  });

  clientOptions = [{ description: '---', value: 'x' }];

  diaryTypes = [{ description: '---', value: 'x' }];

  nameOptions = [{ description: '---', value: 'x' }];

  /** Options for Additional follow-up users, excluding the selected Follow-up user (same user cannot be in both). */
  get additionalUsersOptions(): ListItem[] {
    const followUp = this.fg?.controls?.followupUser?.value;
    if (followUp == null || followUp === '') return this.nameOptions;
    return this.nameOptions.filter((o) => String(o.value) !== String(followUp));
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit() {
    const data = this.storage.get('current-diary');
    this.backRouterLink = data?.backRouterLink ?? this.backRouterLink;
    this.backLabel = data?.backLabel ?? this.backLabel;
    this.isClaimSpecific = data?.isClaimSpecific || false;

    const list = await this.api.list.get();

    this.clientOptions = list.clients;

    this.fg.controls.client.valueChanges.subscribe((value: any) => {
      this.onSelectClient(value);
    });

    this.fg.controls.followupUser.valueChanges.subscribe(
      (followUpUser: string) => {
        const selected = this.fg.controls.selectedUsers.value;
        if (
          followUpUser &&
          Array.isArray(selected) &&
          selected.some((id) => String(id) === String(followUpUser))
        ) {
          this.fg.controls.selectedUsers.setValue(
            selected.filter((id) => String(id) !== String(followUpUser)),
            { emitEvent: false },
          );
        }
        this.clearFollowupDuplicateError();
      },
    );
    this.fg.controls.selectedUsers.valueChanges.subscribe(() => {
      this.clearFollowupDuplicateError();
    });

    if (this.clientOptions.length === 1) {
      this.fg.controls.client.setValue(this.clientOptions[0].value);
    }

    if (data?.isClaimSpecific) {
      this.fg.controls.client.setValue(String(data.clientNumber));
      this.fg.controls.client.disable();
    }

    if (data?.id) {
      this.editMode = true;
      this.isPreloading = true;
      this.currentDiaryId = data.id;
      this.isDiaryTiedToClaim = this.checkIfDiaryTiedToClaim(data);
      const match = data.followUpUser.match(/\(([^)]+)\)/);
      const followUpUserId = match[1];
      this.headerLabel = 'Edit diary';
      const rawClientValue = data.clientId || data.clientNumber;
      const clientValue = String(rawClientValue);
      this.fg.controls.client.setValue(clientValue);

      this.fg.controls.diaryType.setValue(data.diaryTypeId);
      this.fg.controls.followupUser.setValue(followUpUserId);
      this.fg.controls.selectedUsers.setValue(
        data.additionalUsers?.map((x: any) => x.userID),
      );
      this.fg.controls.dueDate.setValue(new Date(data.dueDate));
      this.fg.controls.defaultText.setValue(data.message);
      this.fg.controls.allowOthers.setValue(data.allowOthers);
      this.fg.controls.sendEmail.setValue(data.emailReminder);
      this.fg.controls.completed.setValue(data.isCompleted);
      if (this.isDiaryTiedToClaim) {
        this.fg.controls.client.disable();
      }
      this.allowComplete =
        data.allowOthers ||
        data.createdBy.includes(`(${this.session.user.userName})`);
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onSelectClient(clientNumber: number) {
    this.diaryTypes = [{ description: '---', value: 'x' }];
    this.nameOptions = [{ description: '---', value: 'x' }];
    if (!this.isPreloading) {
      this.fg.controls.diaryType.setValue(null);
      this.fg.controls.followupUser.setValue(null);
      this.fg.controls.selectedUsers.setValue(null);
    }

    const data = this.storage.get('current-diary');
    const generalInfo = await this.api.clientDiary.getGeneralInfo({
      clientNumber: clientNumber,
      claimUID: data?.isClaimSpecific ? data.claimUID : null,
      absenceUID: data?.RecordSource === 'T' ? data.fileNumber : null,
      aDAEventNumber: data?.RecordSource === 'A' ? data.recordId : null,
      leaveNumber: data?.RecordSource === 'L' ? data.fileNumber : null,
    });
    this.diaryTypes = generalInfo.diaryTypes.map((item: any) => ({
      description: item.description,
      value: item.typeUID,
    }));
    this.nameOptions = generalInfo.users.map((item: any) => ({
      description: item.userName,
      value: item.userID,
    }));
    this.isPreloading = false;
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
    errorProperty: 'actionError',
  })
  async onSaveDiary() {
    const followUpUser = this.fg.value.followupUser;
    const additionalUsers = this.fg.value.selectedUsers ?? [];
    const hasDuplicate =
      followUpUser &&
      additionalUsers.some((id) => String(id) === String(followUpUser));
    if (hasDuplicate) {
      this.fg.controls.selectedUsers.setErrors({
        followupDuplicate:
          'The same user cannot be selected for both Follow-up user and Additional follow-up users.',
      });
      this.fg.controls.selectedUsers.markAsTouched();
      this.fg.markAllAsTouched();
      focusFirstInvalid(this.fg, this.formEl?.nativeElement);
      return;
    }
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      focusFirstInvalid(this.fg, this.formEl?.nativeElement);
      return;
    }

    const data = this.storage.get('current-diary');

    const body = {
      diaryUID: this.currentDiaryId,
      followUpUser: this.fg.value.followupUser,
      dueDate: this.fg.value.dueDate,
      typeUID: this.fg.value.diaryType?.value ?? this.fg.value.diaryType,
      completeByCreator: !this.fg.value.allowOthers,
      emailReminder: this.fg.value.sendEmail,
      description: this.fg.value.defaultText,
      tempUser: this.fg.value.tempUser,
      additionalUsers: this.fg.value.selectedUsers ?? [],
      completed: this.fg.value.completed,
      clientNumber: data?.isClaimSpecific
        ? this.view.currentClaim()?.contractNumber
        : this.fg.value.client,
      ...(data?.isClaimSpecific && {
        ...this.getClaimSpecificFields(),
      }),
    };

    if (!this.editMode) {
      await this.api.clientDiary.createDiary(body);
    } else {
      await this.api.clientDiary.updateDiary(body);
    }

    this.clientDiaryData.notifyChanged();
    this.router.navigate([this.backRouterLink]);
  }

  private clearFollowupDuplicateError(): void {
    const followUpUser = this.fg?.controls?.followupUser?.value;
    const selected = this.fg?.controls?.selectedUsers?.value ?? [];
    const hasDuplicate =
      followUpUser &&
      selected.some((id) => String(id) === String(followUpUser));
    if (!hasDuplicate) {
      const err = this.fg?.controls?.selectedUsers?.errors;
      if (err?.['followupDuplicate']) {
        const { followupDuplicate: _, ...rest } = err;
        this.fg.controls.selectedUsers.setErrors(
          Object.keys(rest).length ? rest : null,
          { emitEvent: false },
        );
      }
    }
  }

  private checkIfDiaryTiedToClaim(data: any): boolean {
    return !!(
      data?.RecordSource &&
      (data?.claimUID || data?.recordId) &&
      ['J', 'T', 'A', 'L', 'R'].includes(data.RecordSource)
    );
  }

  private getClaimSpecificFields(): any {
    const claim = this.view.currentClaim();
    if (!claim) {
      return {
        claimUID: null,
        absenceUID: null,
        aDAEventNumber: null,
        leaveNumber: null,
        recordId: null,
      };
    }

    switch (claim.recordSource) {
      case 'J':
        return {
          claimUID: claim.recordId,
          absenceUID: null,
          aDAEventNumber: null,
          leaveNumber: null,
          recordId: claim.recordId,
        };
      case 'T':
        return {
          claimUID: null,
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid,
          aDAEventNumber: null,
          leaveNumber: null,
          recordId: claim.recordId,
        };
      case 'A':
        return {
          claimUID: null,
          absenceUID: null,
          aDAEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
          leaveNumber: null,
          recordId: claim.recordId,
        };
      case 'L':
        return {
          claimUID: null,
          absenceUID: null,
          aDAEventNumber: null,
          leaveNumber: numericAdaOrLeaveKeyFromClaim(claim),
          recordId: claim.recordId,
        };
      default:
        return {
          claimUID: claim.recordId,
          absenceUID: null,
          aDAEventNumber: null,
          leaveNumber: null,
          recordId: claim.recordId,
        };
    }
  }
}
